import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PieImage } from 'components/common'
import { ContentModal } from 'components/organisms/modals/content-modal'
import SocialShareBlock from 'components/organisms/social-share-block'
import { Button } from 'components/atoms/button'

interface ShareSuccessModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	rewardName?: string
	avatarUrl?: string
	coverAvatarUrl?: string
	offerId?: string
	clubId?: string
	onClose?: () => void
}

export function ShareSuccessModal({
	open,
	setOpen,
	rewardName,
	avatarUrl,
	coverAvatarUrl,
	offerId,
	clubId,
	onClose,
}: ShareSuccessModalProps) {
	const navigate = useNavigate()

	const handleClose = () => {
		setOpen(false)
		onClose?.()
	}

	const handleReturnToManage = () => {
		navigate(`/manage/rep-clubs/${clubId}`, { replace: true })
	}

	const handleViewReward = () => {
		navigate(`/manage/rep-clubs/${clubId}/offers/${offerId}`, { replace: true })
	}

	return (
		<ContentModal
			open={open}
			setOpen={handleClose}
			isModal
			onClickClose={() => navigate(`/manage/rep-clubs/${clubId}/offers`, { replace: true })}
			title="Congratulations!"
		>
			<div className="relative flex flex-col items-center space-y-5">
				<div className="max-w-[304px]">
					<div>
						<div>You have successfully created the {rewardName} reward.</div>
					</div>
					<div className="flex justify-center items-center py-8">
						<PieImage
							className="w-[200px] h-auto rounded-xl overflow-hidden flex-shrink-0"
							images={[avatarUrl || coverAvatarUrl || '']}
						/>
					</div>
					<div className="text-primary-text">Share with your community</div>
					<div className="mt-3 mb-6">
						<SocialShareBlock
							className="!justify-center"
							url={`${import.meta.env.VITE_SHARE_URL}/c/${clubId}/o/${offerId}`}
						/>
					</div>
				</div>
				<div className="flex flex-col gap-4">
					<Button variant="primary" onClick={handleViewReward} fullWidth>
						View Reward
					</Button>
					<Button variant="secondary" onClick={handleReturnToManage} fullWidth>
						Return to Manage Club
					</Button>
				</div>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface ShareSuccessModalManagedProps {
	rewardName?: string
	avatarUrl?: string
	coverAvatarUrl?: string
	offerId?: string
	clubId?: string
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'share-success': ShareSuccessModalManagedProps
	}
}

export function ManagedShareSuccessModal({
	onClose,
	...rest
}: ShareSuccessModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<ShareSuccessModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onClose={handleClose}
			{...rest}
		/>
	)
}
